import React, {useState} from 'react';
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar";
import {format} from "date-fns";
import {formatDatetime} from "@/lib/utils";
import {Input} from "@/components/ui/input";
import {Form, FormControl, FormField, FormItem, FormMessage} from "@/components/ui/form";
import {z} from "zod";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {fr} from "date-fns/locale";
import {Skeleton} from "@/components/ui/skeleton";
import {AudioPlayerMusic} from "@/types/types";
import {IconCalendar} from "@tabler/icons-react";

const CKoiLeTitre = () => {
  const [dateTime, setDateTime] = useState<Date>(new Date())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [results, setResults] = useState<AudioPlayerMusic[]>([])

  const fetchMusic = (dateTime) => {
    fetch(process.env.NEXT_PUBLIC_DRUPAL_BASE_URL + '/api/winmedia/songs' + '?' + new URLSearchParams({
      date: dateTime.toISOString()
    }))
      .then((res) => res.json())
      .then((data) => {
        setResults(data.data)
        setIsLoading(false)
      })
      .catch(e => console.error(e))
  }

  const formSchema = z.object({
    date: z.number(),
    time: z.string(),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      date: dateTime.getTime(),
      time: (dateTime.getHours().toString().padStart(2, '0') + ':' + dateTime.getMinutes().toString().padStart(2, '0')),
    },
  })

  const {
    setValue,
    handleSubmit,
  } = form;

  const updateDate = (date) => {
    setDateTime(date)
    setValue('date', date.getTime());
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true)
    const time = values.time
    dateTime.setHours(parseInt(time.slice(0,2)))
    dateTime.setMinutes(parseInt(time.slice(3)))
    fetchMusic(dateTime)
  }

  const datePicker = () => {
    return (
      <div className="flex-1">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"input"}
              display={"flex"}
              size={"input"}
              className="w-full"
            >
              {dateTime ? format(dateTime, "dd MMM y", {locale: fr}) : <span className="text-neutral-500">Sélectionnez la date</span>}
              <IconCalendar size={18} className="ml-5 text-accent-foreground"/>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              locale={fr}
              selected={dateTime}
              onSelect={e => updateDate(e)}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input type="number" {...field} className='hidden' />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      )
  }

  const timePicker = () => {
    return (
      <div className="flex-1">
        <FormField
          control={form.control}
          name="time"
          render={({field}) => (
            <FormItem>
              <FormControl>
                <Input type="time" className="" {...field} />
              </FormControl>
              <FormMessage/>
            </FormItem>
          )}
        />
      </div>
    )
  }

  return (
    <div className="bg-lightBlue border px-6 py-8">
      <div className="mb-6">
        <h2 className="text-[28px] mb-4 text-foreground-dark">C’est quoi ce titre ?</h2>
        <p>Vous avez adoré un morceau à la radio mais vous n’en connaissez pas le titre ? Utilisez notre moteur de recherche.</p>
      </div>

      <div>
        <Form {...form}>
          <form className="flex max-md:flex-col gap-y-3 gap-x-2" onSubmit={handleSubmit(onSubmit)}>
            {datePicker()}

            {timePicker()}

            <Button>Rechercher</Button>
          </form>
        </Form>
      </div>

      <div className="flex flex-col gap-4 mt-6">
        {isLoading && (
          <>
            <div className="flex max-md:flex-col gap-y-1 justify-between">
              <Skeleton variant="contrast" className="h-7 w-[200px]"/>
              <Skeleton variant="contrast" className="h-5 w-[128px]"/>
            </div>
          </>
        )}

        {!isLoading && results && results.map((item, index) => (
          <div key={"ckoiletitre" + index} className="flex max-md:flex-col gap-y-1 justify-between"><span className="text-lg">{item.title + " - " + item.artists}</span>
            <span className="text-sm">{formatDatetime(item.startAt)}</span></div>
        ))}
      </div>
    </div>
  );
};

export default CKoiLeTitre;
